<script lang="ts" setup>
const props = defineProps<{
  faqData: FAQData;
}>();

const activeTitle = ref<string>(Object.keys(props.faqData)[0] || "");
const openItems = ref<Set<string>>(new Set());

const setActiveTitle = (titleKey: string) => {
  activeTitle.value = titleKey;
  openItems.value.clear();
};

const toggleItem = (itemKey: string) => {
  if (openItems.value.has(itemKey)) {
    openItems.value.delete(itemKey);
  } else {
    openItems.value.clear();
    openItems.value.add(itemKey);
  }
};

const isItemOpen = (itemKey: string) => openItems.value.has(itemKey);

const getQuestionsForTitle = (titleKey: string) => {
  const title = props.faqData[titleKey];
  if (!title) return [];

  return Object.entries(title)
    .filter(
      ([key, value]) => key.startsWith("question") && typeof value === "object"
    )
    .map(([key, value]) => ({ key: key as string, ...(value as any) }));
};

const getSectionId = (titleKey: string) => {
  return `faq-section-${String(titleKey).toLowerCase().replace(/\s+/g, "-")}`;
};
</script>

<template>
  <div class="w-full">
    <h1 class="text-2.5xl font-bold text-neutral-200 my-7">
      {{ $t("main.referrals.notInfluencerFAQTitle") }}
    </h1>

    <div class="block md:hidden">
      <div class="space-y-8">
        <div
          v-for="(titleData, titleKey) in props.faqData"
          :key="titleKey"
          :id="getSectionId(String(titleKey))"
          class="w-full scroll-mt-20">
          <h3 class="text-xl font-semibold text-primary-500 mb-4">
            {{ titleData.title }}
          </h3>

          <div class="space-y-4">
            <template
              v-for="question in getQuestionsForTitle(String(titleKey))"
              :key="question.key">
              <div
                class="w-full bg-neutral-800 text-neutral-200 border border-primary-900 rounded-lg">
                <div
                  class="py-4 px-6 flex justify-between cursor-pointer"
                  @click="toggleItem(question.key)">
                  <h3 class="text-lg font-semibold text-neutral-200">
                    {{ question.question }}
                  </h3>

                  <NuxtIcon
                    name="mdi:keyboard-arrow-down"
                    class="w-7 h-7 text-neutral-200 transition-all duration-300 cursor-pointer shrink-0"
                    :class="{
                      'transform rotate-180': isItemOpen(question.key),
                    }" />
                </div>
                <transition
                  enter-active-class="transition-all duration-300 ease-in-out"
                  enter-from-class="max-h-0 opacity-0"
                  enter-to-class="max-h-[1000px] opacity-100"
                  leave-active-class="transition-all duration-300 ease-in-out"
                  leave-from-class="max-h-[1000px] opacity-100"
                  leave-to-class="max-h-0 opacity-0">
                  <div
                    v-if="isItemOpen(question.key)"
                    class="w-full overflow-hidden rounded-b-lg bg-neutral-800">
                    <div class="border-t border-primary-900 px-6 py-6">
                      <div
                        v-if="question.applicability"
                        class="flex items-center gap-2 text-lg font-semibold italic text-neutral-400">
                        <span
                          >{{
                            $t("main.referrals.notInfluencerFAQApplicability")
                          }}
                          {{ question.applicability }}</span
                        >
                      </div>
                      <div class="prose prose-invert max-w-none">
                        <div
                          v-html="question.answer"
                          class="space-y-2 leading-relaxed text-neutral-300"></div>
                      </div>
                    </div>
                  </div>
                </transition>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>

    <div class="hidden md:flex gap-8">
      <div class="w-1/3 space-y-10">
        <div
          v-for="(titleData, titleKey) in props.faqData"
          :key="titleKey"
          class="w-full">
          <button
            @click="setActiveTitle(titleKey as string)"
            class="w-full text-left transition-all duration-200">
            <h3
              class="text-lg font-semibold cursor-pointer"
              :class="{
                'text-primary-500': activeTitle === titleKey,
                'text-neutral-200': activeTitle !== titleKey,
              }">
              {{ titleData.title }}
            </h3>
          </button>
        </div>
      </div>

      <div class="w-2/3 space-y-5">
        <template v-if="activeTitle">
          <template
            v-for="question in getQuestionsForTitle(activeTitle)"
            :key="question.key">
            <div
              class="w-full bg-neutral-800 text-neutral-200 border border-primary-900 rounded-lg">
              <div
                class="py-4 px-6 flex justify-between cursor-pointer"
                @click="toggleItem(question.key)">
                <h3 class="text-lg font-semibold text-neutral-200">
                  {{ question.question }}
                </h3>

                <NuxtIcon
                  name="mdi:keyboard-arrow-down"
                  class="w-7 h-7 text-neutral-200 transition-all duration-300 cursor-pointer shrink-0"
                  :class="{
                    'transform rotate-180': isItemOpen(question.key),
                  }" />
              </div>
              <transition
                enter-active-class="transition-all duration-300 ease-in-out"
                enter-from-class="max-h-0 opacity-0"
                enter-to-class="max-h-[1000px] opacity-100"
                leave-active-class="transition-all duration-300 ease-in-out"
                leave-from-class="max-h-[1000px] opacity-100"
                leave-to-class="max-h-0 opacity-0">
                <div
                  v-if="isItemOpen(question.key)"
                  class="w-full overflow-hidden rounded-b-lg bg-neutral-800">
                  <div class="border-t border-primary-900 px-6 py-6">
                    <div
                      v-if="question.applicability"
                      class="flex items-center gap-2 text-lg font-semibold italic text-neutral-400">
                      <span
                        >{{
                          $t("main.referrals.notInfluencerFAQApplicability")
                        }}
                        {{ question.applicability }}</span
                      >
                    </div>
                    <div class="prose prose-invert max-w-none">
                      <div
                        v-html="question.answer"
                        class="space-y-2 leading-relaxed text-neutral-300"></div>
                    </div>
                  </div>
                </div>
              </transition>
            </div>
          </template>
        </template>
      </div>
    </div>
  </div>
</template>
